import axios, { AxiosError } from 'axios';
import { attachIdempotencyKey, enqueueFromAxiosError } from '../utils/apiWakeQueue';
import { isSnapshotCurrent, resetSessionScope, scopeSnapshot, ScopeChangedError } from '../utils/sessionScope';
import {
  ACTING_HEADER, ACTING_RESPONSE_HEADER, ActingCompanyMismatchError, clearActingCompany,
  getActingCompany, isGlobalAdminSession, notifyGdprHidden,
} from '../utils/actingCompany';

const getBaseURL = () => {
  const configuredApiUrl = process.env.REACT_APP_API_URL?.trim();
  if (configuredApiUrl) {
    return configuredApiUrl.endsWith('/')
      ? configuredApiUrl.slice(0, -1)
      : configuredApiUrl;
  }

  const legacyBaseUrl = process.env.REACT_APP_API_BASE_URL?.trim();
  if (legacyBaseUrl) {
    return `${legacyBaseUrl.replace(/\/$/, '')}/api`;
  }

  const hostname = window.location.hostname;
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return '/api';
  }

  // On public deployments, prefer same-origin API to avoid mixed-content and port issues.
  return '/api';
};

const api = axios.create({
  baseURL: getBaseURL(),
  headers: {
    'Content-Type': 'application/json',
  },
});

api.interceptors.request.use((config) => {
  const language = localStorage.getItem('language') || 'hr';
  config.headers['Accept-Language'] = language;
  // SEC-02: zapamti sesiju u kojoj je zahtjev nastao; ponovljeni zahtjev iz reda
  // mora odgovarati trenutno aktivnoj sesiji, inače se ne šalje.
  if (!config.zomaSession) config.zomaSession = scopeSnapshot();
  if (config.zomaStrictSession && !isSnapshotCurrent(config.zomaSession)) throw new ScopeChangedError();
  // Posao 23-7: globalni admin šalje firmu rada ove kartice (X-Zoma-Company) i nema offline red (odluka 13).
  if (isGlobalAdminSession()) {
    const acting = config.zomaActingCompany !== undefined ? config.zomaActingCompany : getActingCompany()?.id ?? null;
    config.zomaActingSent = acting;
    if (acting) config.headers[ACTING_HEADER] = acting;
    config.skipWakeQueue = true;
  }
  // Posao 15D: operacije iz offline kataloga dobivaju Idempotency-Key prije prvog slanja.
  return attachIdempotencyKey(config);
});

const responseCompany = (headers: unknown): string | null => {
  const value = (headers as Record<string, unknown> | undefined)?.[ACTING_RESPONSE_HEADER];
  return typeof value === 'string' && value ? value : null;
};

api.interceptors.response.use(
  (response) => {
    // Posao 23-7: odgovor mora biti za firmu u kojoj je zahtjev poslan.
    const sent = response.config?.zomaActingSent;
    const received = responseCompany(response.headers);
    if (sent && received && received.toLowerCase() !== sent.toLowerCase()) return Promise.reject(new ActingCompanyMismatchError());
    return response;
  },
  (error: AxiosError) => {
    const data = error.response?.data as { code?: string; category?: string } | undefined;
    if (isGlobalAdminSession() && data?.code === 'COMPANY_NOT_FOUND') clearActingCompany();
    if (isGlobalAdminSession() && data?.code === 'GDPR_HIDDEN') notifyGdprHidden(typeof data.category === 'string' ? data.category : null);
    const requestUrl = error.config?.url || '';
    const isLoginRequest = requestUrl.includes('/auth/login');

    // Zakašnjeli 401 iz prethodne sesije ne smije odjaviti trenutno prijavljenog korisnika.
    const origin = error.config?.zomaSession;
    const fromPreviousSession = Boolean(origin && origin.epoch !== scopeSnapshot().epoch);
    if (error.response?.status === 401 && !isLoginRequest && !fromPreviousSession) {
      resetSessionScope(null);
      localStorage.removeItem('token');
      window.location.href = '/login';
      return Promise.reject(error);
    }
    enqueueFromAxiosError(error);
    return Promise.reject(error);
  }
);

export default api;
